import React from 'react';
import Navigation from '../../components/Nav/Nav';
import { Footer } from '../../LandingPage';
import tony2 from '../../resources/tony-wedding.jpg';
import team from '../../resources/team.png';
import draperLogo from '../../resources/draper-logo.png';
import hackerNewsLogo from '../../resources/social/hacker-news-logo.webp';
import productHuntLogo from '../../resources/social/product-hunt-logo.webp';
import twitterLogo from '../../resources/social/twitter-logo.webp';
import { useMediaQuery } from 'react-responsive';
import { below } from '../../breakpoints';
import { Check } from 'react-feather';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faShieldAlt, faChartLine, faUsers } from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

const CtaButton = () => {
  const history = useHistory();
  return (
    <button
      className="tw-bg-gradient-to-r tw-from-primary-500 tw-to-secondary-500 tw-text-white tw-font-semibold tw-py-3 tw-px-6 tw-text-lg tw-rounded-lg tw-shadow-md tw-flex tw-items-center tw-justify-center tw-transition-all tw-duration-300 hover:tw-shadow-xl hover:tw-scale-105 tw-w-full lg:tw-w-auto"
      onClick={() => history.push('/home')}
    >
      <FontAwesomeIcon icon={faUser as IconProp} className="tw-mr-2" />
      Start Free Trial
    </button>
  );
};

const SocialProofLogos = () => {
  const logos = [
    { src: hackerNewsLogo, alt: 'Hacker News', link: 'https://news.ycombinator.com/item?id=34595531' },
    { src: productHuntLogo, alt: 'Product Hunt', link: 'https://www.producthunt.com/products/jumble-journal' },
    { src: twitterLogo, alt: 'Twitter', link: 'https://x.com/JumbleJournal' },
    { src: draperLogo, alt: 'Draper University', link: 'https://draperuniversity.com/' }
  ];

  return (
    <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-8 tw-items-center tw-mt-8">
      {logos.map((logo) => (
        <a key={logo.alt} href={logo.link} target="_blank" rel="noopener noreferrer">
          <img
            src={logo.src}
            alt={logo.alt}
            className="tw-h-8 tw-w-auto tw-grayscale tw-opacity-60 hover:tw-opacity-100 hover:tw-grayscale-0 tw-transition-all tw-duration-300"
          />
        </a>
      ))}
    </div>
  );
};

const StatsGrid = () => {
  const stats = [
    { icon: faUsers, number: '1,000+', label: 'Active Journalers' },
    { icon: faChartLine, number: '5M+', label: 'Words Written' },
    { icon: faShieldAlt, number: '100K+', label: 'Insights Discovered' }
  ];

  return (
    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-6 lg:tw-gap-8 tw-mt-12">
      {stats.map((stat, index) => (
        <div key={index} className="tw-text-center tw-bg-white tw-p-6 tw-rounded-xl tw-shadow-sm">
          <div className="tw-flex tw-justify-center tw-mb-4">
            <FontAwesomeIcon icon={stat.icon as IconProp} className="tw-text-3xl tw-text-primary-500" />
          </div>
          <div className="tw-text-3xl tw-font-bold tw-text-primary-900 tw-mb-2">{stat.number}</div>
          <div className="tw-text-sm tw-text-neutral-600">{stat.label}</div>
        </div>
      ))}
    </div>
  );
};

const AboutUs = () => {
  const isTabletOrMobile = useMediaQuery(below('lg'));
  return (
    <div className="tw-col-start-2 tw-col-end-12">
      <Navigation />

      {/* Hero Section - Customer Focused */}
      <div className="tw-bg-gradient-to-r tw-from-primary-50 tw-to-secondary-50 tw-rounded-2xl tw-py-16 tw-px-8 tw-mt-24 tw-text-center">
        <div className="tw-max-w-4xl tw-mx-auto">
          <div className="tw-text-sm tw-bg-gradient-to-b tw-from-primary-500 tw-to-secondary-500 tw-bg-clip-text tw-text-transparent tw-mb-4">
            WHY WE BUILT JUMBLE
          </div>
          <h1 className="tw-text-3xl lg:tw-text-5xl tw-font-bold tw-text-primary-900 tw-mb-6">
            We Turned 20 Years of Journaling Chaos Into Your Clarity System
          </h1>
          <p className="tw-text-lg lg:tw-text-xl tw-text-neutral-700 tw-mb-8 tw-leading-relaxed">
            Like you, we struggled with scattered thoughts, overwhelming decisions, and lost insights buried in endless
            journal pages. That's why we created Jumble—to transform your journaling from a pile of forgotten entries
            into your personal growth engine.
          </p>

          <div className="tw-flex tw-flex-col lg:tw-flex-row tw-items-center tw-justify-center tw-gap-6">
            <CtaButton />
            <ul className="tw-flex tw-flex-wrap tw-justify-center tw-gap-x-4 tw-gap-y-2 tw-list-none tw-p-0 tw-m-0 tw-text-sm tw-text-neutral-600">
              {['Join 1,000+ users', 'Cancel anytime', 'No questions asked refund'].map((claim) => (
                <li key={claim} className="tw-flex tw-items-center tw-gap-1.5">
                  <Check className="tw-w-4 tw-h-4 tw-shrink-0 tw-text-primary-600" />
                  {claim}
                </li>
              ))}
            </ul>
          </div>

          <StatsGrid />
        </div>
      </div>

      {/* Origin Story Section */}
      <div className="tw-py-16 tw-px-8">
        <div className="tw-max-w-6xl tw-mx-auto">
          <div className="tw-text-center tw-mb-12">
            <h2 className="tw-text-sm tw-bg-gradient-to-b tw-from-primary-400 tw-to-secondary-500 tw-bg-clip-text tw-text-transparent tw-mb-4">
              OUR STORY
            </h2>
            <h3 className="tw-text-3xl lg:tw-text-4xl tw-font-bold tw-text-primary-900 tw-mb-6">
              From Personal Frustration to Your Solution
            </h3>
          </div>

          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-[minmax(0,1fr)_minmax(0,1fr)] tw-gap-12 tw-items-center">
            <div>
              <p className="tw-text-lg tw-text-neutral-700 tw-mb-6">
                <strong>"I had thousands of journal entries, but I couldn't find the patterns that mattered."</strong>
              </p>
              <p className="tw-text-base tw-text-neutral-700 tw-mb-4">
                After 20 years of journaling, I realized I was trapped in the same cycles, making the same mistakes, and
                missing the insights that could change everything. My journal was a graveyard of forgotten wisdom.
              </p>
              <p className="tw-text-base tw-text-neutral-700 tw-mb-4">
                That's when I decided to build what I wish I'd had from day one: an AI-powered system that doesn't just
                store your thoughts—it reveals the hidden patterns, surfaces breakthroughs, and converts the chaos of
                life into clear actionable insights.
              </p>
              <p className="tw-text-base tw-text-neutral-700">
                <strong>- Tony, Founder & Lifelong Journaler</strong>
              </p>
            </div>

            <div className="tw-text-center">
              {isTabletOrMobile ? (
                <img
                  alt="Tony, Founder of Jumble Journal"
                  className="tw-w-64 tw-h-64 tw-mx-auto tw-rounded-full tw-object-cover tw-shadow-lg"
                  src={tony2}
                />
              ) : (
                <img
                  alt="Jumble Journal Team"
                  className="tw-w-full tw-max-w-md tw-mx-auto tw-rounded-xl tw-shadow-lg"
                  src={team}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* FIRE Framework Section */}
      <div className="tw-bg-neutral-50 tw-rounded-2xl tw-py-16 tw-px-8">
        <div className="tw-max-w-4xl tw-mx-auto tw-text-center">
          <h2 className="tw-text-sm tw-bg-gradient-to-b tw-from-primary-400 tw-to-secondary-500 tw-bg-clip-text tw-text-transparent tw-mb-4">
            OUR PROVEN METHOD
          </h2>
          <h3 className="tw-text-3xl lg:tw-text-4xl tw-font-bold tw-text-primary-900 tw-mb-6">
            The FIRE Framework: Your Personal Growth Engine
          </h3>
          <p className="tw-text-lg tw-text-neutral-700 tw-mb-8">
            Our proprietary FIRE Framework isn't just another journaling method—it's a structured system developed from
            20 years of personal journaling and thousands of user conversations. It captures your thoughts, identifies
            patterns, and surfaces meaningful insights to accelerate your personal growth.
          </p>

          <a
            href="https://fire-framework.tonycodes.com"
            target="_blank"
            rel="noopener noreferrer"
            className="tw-inline-flex tw-items-center tw-bg-gradient-to-r tw-from-primary-500 tw-to-secondary-500 tw-text-white tw-font-semibold tw-py-3 tw-px-6 tw-text-lg tw-rounded-lg tw-shadow-md tw-transition-all tw-duration-300 hover:tw-shadow-xl hover:tw-scale-[1.02] tw-no-underline hover:tw-text-white"
          >
            Learn About Our FIRE Framework
            <svg className="tw-w-5 tw-h-5 tw-ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </a>
        </div>
      </div>

      {/* Trust & Security Section */}
      <div className="tw-bg-primary-800 tw-text-white tw-rounded-2xl tw-py-16 tw-px-8">
        <div className="tw-max-w-4xl tw-mx-auto tw-text-center">
          <h2 className="tw-text-sm tw-bg-gradient-to-b tw-from-primary-400 tw-to-secondary-400 tw-bg-clip-text tw-text-transparent tw-mb-4">
            SECURITY & TRUST
          </h2>
          <h3 className="tw-text-3xl lg:tw-text-4xl tw-font-bold tw-mb-6">
            Your Thoughts Are Safe. Your Growth Is Guaranteed.
          </h3>

          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-8 tw-mt-12">
            <div className="tw-text-center">
              <FontAwesomeIcon icon={faShieldAlt as IconProp} className="tw-text-4xl tw-text-secondary-400 tw-mb-4" />
              <h4 className="tw-text-lg tw-font-semibold tw-mb-2">Secure Architecture</h4>
              <p className="tw-text-sm tw-text-neutral-200">
                Your journal entries are encrypted in-transit and at-rest and protected by industry standard security
                protocols.
              </p>
            </div>

            <div className="tw-text-center">
              <FontAwesomeIcon icon={faUsers as IconProp} className="tw-text-4xl tw-text-secondary-400 tw-mb-4" />
              <h4 className="tw-text-lg tw-font-semibold tw-mb-2">Trusted by Thousands</h4>
              <p className="tw-text-sm tw-text-neutral-200">
                1,000+ active users trust us with their most private thoughts and biggest breakthroughs.
              </p>
            </div>

            <div className="tw-text-center">
              <FontAwesomeIcon icon={faChartLine as IconProp} className="tw-text-4xl tw-text-secondary-400 tw-mb-4" />
              <h4 className="tw-text-lg tw-font-semibold tw-mb-2">Proven Results</h4>
              <p className="tw-text-sm tw-text-neutral-200">
                100,000+ insights discovered and 5 million+ words of transformative journaling.
              </p>
            </div>
          </div>

          <SocialProofLogos />
        </div>
      </div>

      {/* Final CTA Section */}
      <div className="tw-py-16 tw-px-8 tw-text-center">
        <div className="tw-max-w-4xl tw-mx-auto">
          <h2 className="tw-text-3xl lg:tw-text-4xl tw-font-bold tw-text-primary-900 tw-mb-6">
            Ready to Transform Your Journaling Journey?
          </h2>
          <p className="tw-text-lg tw-text-neutral-700 tw-mb-8">
            Join thousands of journalers who've moved from chaos to clarity. Start your free trial today—no credit card
            required.
          </p>

          <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
            <CtaButton />
            <ul className="tw-flex tw-flex-wrap tw-justify-center tw-gap-x-4 tw-gap-y-2 tw-list-none tw-p-0 tw-m-0 tw-text-sm tw-text-neutral-600">
              {['Free trial', 'Secure Architecture', 'Cancel anytime'].map((claim) => (
                <li key={claim} className="tw-flex tw-items-center tw-gap-1.5">
                  <Check className="tw-w-4 tw-h-4 tw-shrink-0 tw-text-primary-600" />
                  {claim}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AboutUs;
